import { List, Map } from 'immutable';
import { IChangeField } from '../../utils/helpers/model.helper';
import { Attachment } from '../attachments/attachment.model';
import {
  entityKeys,
  entityModelFactory,
  generateContent,
} from '../entity-data/entity.model';
import { Header } from '../entity-data/sub-models/header.model';
import { CompanyInfo } from '../settings-data/sub-models/company-info.model';
import { Tax } from '../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { DOC_ACCEPT_VERSION, DocumentTypesEnum } from './document-data.data';
import { Billing } from './sub-models/billing.model';
import { CalculationResult } from './sub-models/calculation-result.model';
import { DepositPayment } from './sub-models/deposit-payment.model';
import { Deposit } from './sub-models/deposit.model';
import { Discount } from './sub-models/discount.model';
import { DocumentItem } from './sub-models/document-item.model';
import { DocumentPayments } from './sub-models/document-payments.model';
import { DocumentPreview } from './sub-models/document-preview.model';
import { LatestActivity } from './sub-models/latest-activity.model';
import { LinkedDocumentPreview } from './sub-models/linked-document-preview.model';
import { LinkedDocument } from './sub-models/linked-document.model';
import { Links } from './sub-models/links.model';
import { Schedule } from './sub-models/schedule.model';
import { Shipping } from './sub-models/shipping.model';
import { Signature } from './sub-models/signature.model';
import { Sortable } from './sub-models/sortable.model';
import { SourceDocument } from './sub-models/source-document.model';
import { States } from './sub-models/states.model';
import { AchCreditBankDetails } from '../document-data/sub-models/ach-credit-bank-details.model';

export const PARENT_SCHEMA = Object.freeze({
  STATES: States,
  PAYMENTS: DocumentPayments,
  ACH_CREDIT_BANK_DETAILS: null,
  LATEST_CALCULATION_RESULTS: CalculationResult,
  LATEST_ACTIVITY: LatestActivity,
  LINKS: Links,
  SCHEDULE: Schedule, // not existent in server response
  METADATA: null,
  SORTABLE: Sortable,
  SOURCE_DOCUMENT_PREVIEW: null, // DocumentPreview
  LINKED_DOCUMENT_PREVIEWS: List<LinkedDocumentPreview>([]),
  LINKED_DOCUMENTS: List<LinkedDocument>([]),
});
export const CONTENT_SCHEMA = Object.freeze({
  DOC_DATE: '', // i2g-timezoneless-date (yyyy-mm-dd)
  DOC_NUMBER: null, // ''
  BILLING: Billing,
  SHIPPING: null, // Shipping
  SETTINGS: DocumentPresetSettings,
  COMPANY_INFO: CompanyInfo,
  ITEMS: List<DocumentItem>([]),
  TAXES: List<Tax>([]),
  WITHHOLDING_TAX: WithholdingTax,
  DISCOUNT: Discount,
  ATTACHMENTS: List<Attachment>([]),
  CLIENT_SIGNATURE: null, // Signature
  COMPANY_SIGNATURE: null, // Signature
  LEGACY_V3: null,
  CALCULATION_VERSION: '3.0.0',
  SOURCE_DOCUMENT: null, // SourceDocument
  DEPOSIT: Deposit,
  PDF_SIZE: 0,
  FILE_ATTACHMENTS: List<Attachment>([]),
});
const KEYS = entityKeys(PARENT_SCHEMA, CONTENT_SCHEMA);
export const CONTENT_KEYS = generateContent(CONTENT_SCHEMA).KEYS;
const parentChanges = (props: Map<string, any>): IChangeField[] => {
  const headerKey = KEYS.HEADER;
  const schemaVersionKey = Header.KEYS.SCHEMA_VERSION;
  const header = props.get(headerKey) || Map();
  const schemaVersion = header.get(schemaVersionKey);
  const states = props.get(KEYS.STATES);
  const payments = props.get(KEYS.PAYMENTS);
  const latestActivity = props.get(KEYS.LATEST_ACTIVITY);
  const links = props.get(KEYS.LINKS);
  const calculationResult = props.get(KEYS.LATEST_CALCULATION_RESULTS);
  const schedule = props.get(KEYS.SCHEDULE);
  const sortable = props.get(KEYS.SORTABLE);
  const sourceDocumentPreview = props.get(KEYS.SOURCE_DOCUMENT_PREVIEW);
  const linkedDocumentPreviews = props.get(KEYS.LINKED_DOCUMENT_PREVIEWS);
  const linkedDocuments = props.get(KEYS.LINKED_DOCUMENTS);
  const achCreditBankDetails = props.get(KEYS.ACH_CREDIT_BANK_DETAILS);

  return [
    {
      key: headerKey,
      value: new Header(
        schemaVersion
          ? header
          : header.set(schemaVersionKey, DOC_ACCEPT_VERSION)
      ),
    },
    {
      key: KEYS.STATES,
      value: new States(states),
    },
    {
      key: KEYS.PAYMENTS,
      value: payments ? new DocumentPayments(payments) : null,
    },
    {
      key: KEYS.LATEST_ACTIVITY,
      value: new LatestActivity(latestActivity),
    },
    {
      key: KEYS.LINKS,
      value: new Links(links),
    },
    {
      key: KEYS.LATEST_CALCULATION_RESULTS,
      value: new CalculationResult(calculationResult),
    },
    {
      key: KEYS.SCHEDULE,
      value: new Schedule(schedule),
    },
    {
      key: KEYS.SORTABLE,
      value: new Sortable(sortable),
    },
    {
      key: KEYS.SOURCE_DOCUMENT_PREVIEW,
      value: sourceDocumentPreview
        ? new DocumentPreview(sourceDocumentPreview)
        : null,
    },
    {
      key: KEYS.LINKED_DOCUMENT_PREVIEWS,
      value: linkedDocumentPreviews
        ? linkedDocumentPreviews.map(
            (params) => new LinkedDocumentPreview(params)
          )
        : List<LinkedDocumentPreview>([]),
    },
    {
      key: KEYS.LINKED_DOCUMENTS,
      value: linkedDocuments
        ? linkedDocuments.map((params) => new LinkedDocument(params))
        : List<LinkedDocument>([]),
    },
    {
      key: KEYS.ACH_CREDIT_BANK_DETAILS,
      value: achCreditBankDetails
        ? new AchCreditBankDetails(achCreditBankDetails)
        : null,
    },
  ];
};
const contentChanges = (contentProps: Map<string, any>): IChangeField[] => {
  const shipping = contentProps.get(CONTENT_KEYS.SHIPPING);
  const documentItems = contentProps.get(CONTENT_KEYS.ITEMS);
  const taxes = contentProps.get(CONTENT_KEYS.TAXES);
  const withholdingTax = contentProps.get(CONTENT_KEYS.WITHHOLDING_TAX);
  const attachments = contentProps.get(CONTENT_KEYS.ATTACHMENTS);
  const clientSignature = contentProps.get(CONTENT_KEYS.CLIENT_SIGNATURE);
  const companySignature = contentProps.get(CONTENT_KEYS.COMPANY_SIGNATURE);
  const sourceDocument = contentProps.get(CONTENT_KEYS.SOURCE_DOCUMENT);
  const deposit = contentProps.get(CONTENT_KEYS.DEPOSIT);
  const pdfSize = contentProps.get(CONTENT_KEYS.PDF_SIZE);
  const fileAttachments = contentProps.get(CONTENT_KEYS.FILE_ATTACHMENTS);

  return [
    {
      key: CONTENT_KEYS.BILLING,
      value: new Billing(contentProps.get(CONTENT_KEYS.BILLING)),
    },
    {
      key: CONTENT_KEYS.SHIPPING,
      value: shipping ? new Shipping(shipping) : null,
    },
    {
      key: CONTENT_KEYS.COMPANY_INFO,
      value: new CompanyInfo(contentProps.get(CONTENT_KEYS.COMPANY_INFO)),
    },
    {
      key: CONTENT_KEYS.ITEMS,
      value: documentItems
        ? documentItems.map((params) => new DocumentItem(params))
        : List<DocumentItem>([]),
    },
    {
      key: CONTENT_KEYS.TAXES,
      value: taxes ? taxes.map((params) => new Tax(params)) : List<Tax>([]),
    },
    {
      key: CONTENT_KEYS.DISCOUNT,
      value: new Discount(contentProps.get(CONTENT_KEYS.DISCOUNT)),
    },
    {
      key: CONTENT_KEYS.WITHHOLDING_TAX,
      value: withholdingTax ? new WithholdingTax(withholdingTax) : null,
    },
    {
      key: CONTENT_KEYS.SETTINGS,
      value: new DocumentPresetSettings(
        contentProps.get(CONTENT_KEYS.SETTINGS)
      ),
    },
    {
      key: CONTENT_KEYS.ATTACHMENTS,
      value: attachments
        ? attachments.map((params) => new Attachment(params))
        : List<Attachment>([]),
    },
    {
      key: CONTENT_KEYS.CLIENT_SIGNATURE,
      value: clientSignature ? new Signature(clientSignature) : null,
    },
    {
      key: CONTENT_KEYS.COMPANY_SIGNATURE,
      value: companySignature ? new Signature(companySignature) : null,
    },
    {
      key: CONTENT_KEYS.SOURCE_DOCUMENT,
      value: sourceDocument ? new SourceDocument(sourceDocument) : null,
    },
    {
      key: CONTENT_KEYS.DEPOSIT,
      value: deposit ? new Deposit(deposit) : null,
    },
    {
      key: CONTENT_KEYS.PDF_SIZE,
      value: pdfSize || null,
    },
    {
      key: CONTENT_KEYS.FILE_ATTACHMENTS,
      value: fileAttachments
        ? fileAttachments.map((params) => new Attachment(params))
        : List<Attachment>([]),
    },
  ];
};

export class Document extends entityModelFactory(
  PARENT_SCHEMA,
  parentChanges,
  CONTENT_SCHEMA,
  contentChanges
) {
  static ENTITY_TYPE: string = 'document';
  static KEYS: any = KEYS;
  static CONTENT_KEYS: any = CONTENT_KEYS;

  get ENTITY_TYPE(): string {
    return Document.ENTITY_TYPE;
  }

  get sortable(): Sortable {
    return this.get(KEYS.SORTABLE);
  }

  get documentType(): DocumentTypesEnum {
    return this.header.type as any;
  }

  get overallState(): string {
    return this.states.overall;
  }

  get dueDate(): string {
    return this.states.dueDate;
  }

  get billingName(): string {
    return this.billing.name;
  }

  get billingEmail(): string {
    return this.billing.email;
  }

  get paymentDetails(): string {
    return this.settings.paymentDetails;
  }

  get companyInfo(): CompanyInfo {
    return this.getFieldInContent(CONTENT_KEYS.COMPANY_INFO);
  }

  get docNumber(): string {
    return this.getFieldInContent(CONTENT_KEYS.DOC_NUMBER);
  }

  get settings(): DocumentPresetSettings {
    return this.getFieldInContent(CONTENT_KEYS.SETTINGS);
  }

  get documentItems(): List<DocumentItem> {
    return this.getFieldInContent(CONTENT_KEYS.ITEMS);
  }

  get taxes(): List<Tax> {
    return this.getFieldInContent(CONTENT_KEYS.TAXES);
  }

  get billing(): Billing {
    return this.getFieldInContent(CONTENT_KEYS.BILLING);
  }

  get discount(): Discount {
    return this.getFieldInContent(CONTENT_KEYS.DISCOUNT);
  }

  get withholdingTax(): WithholdingTax {
    return this.getFieldInContent(CONTENT_KEYS.WITHHOLDING_TAX);
  }

  get shipping(): Shipping {
    return this.getFieldInContent(CONTENT_KEYS.SHIPPING);
  }

  get docDate(): string {
    return this.getFieldInContent(CONTENT_KEYS.DOC_DATE);
  }

  get datePaid(): string {
    return this.states.datePaid;
  }

  get states(): States {
    return this.get(KEYS.STATES);
  }

  get payments(): DocumentPayments {
    return this.get(KEYS.PAYMENTS);
  }

  get latestActivity(): LatestActivity {
    return this.get(KEYS.LATEST_ACTIVITY);
  }

  get links(): Links {
    return this.get(KEYS.LINKS);
  }

  get calculationResult(): CalculationResult {
    return this.get(KEYS.LATEST_CALCULATION_RESULTS);
  }

  get terms(): number {
    return this.settings.terms;
  }

  get attachments(): List<Attachment> {
    return this.getFieldInContent(CONTENT_KEYS.ATTACHMENTS);
  }

  get schedule(): Schedule {
    return this.get(KEYS.SCHEDULE);
  }

  get clientSignature(): Signature {
    return this.getFieldInContent(CONTENT_KEYS.CLIENT_SIGNATURE);
  }

  get companySignature(): Signature {
    return this.getFieldInContent(CONTENT_KEYS.COMPANY_SIGNATURE);
  }

  get sourceDocument(): SourceDocument {
    return this.getFieldInContent(CONTENT_KEYS.SOURCE_DOCUMENT);
  }

  get sourceDocumentPreview(): DocumentPreview {
    return this.get(KEYS.SOURCE_DOCUMENT_PREVIEW);
  }

  get linkedDocumentPreviews(): List<LinkedDocumentPreview> {
    return this.get(KEYS.LINKED_DOCUMENT_PREVIEWS);
  }

  get linkedDocuments(): List<LinkedDocument> {
    return this.get(KEYS.LINKED_DOCUMENTS);
  }

  get deposit(): Deposit {
    return this.getFieldInContent(CONTENT_KEYS.DEPOSIT);
  }

  get pdfSize(): number {
    return this.getFieldInContent(CONTENT_KEYS.PDF_SIZE);
  }

  get fileAttachments(): List<Attachment> {
    return this.getFieldInContent(CONTENT_KEYS.FILE_ATTACHMENTS);
  }

  serialize(): Document {
    const serializedEntity = super.serialize();

    return serializedEntity.toMap().remove(KEYS.SCHEDULE) as Document;
  }

  referencesPopulated(): boolean {
    const billingReference = this.billing.reference;
    const billingRefPopulated: boolean = billingReference
      ? billingReference.hasValue()
      : true;
    const docItemRefsPopulated = !this.documentItems.some(
      (documentItem: DocumentItem) => {
        const docItemReference = documentItem.reference;

        return docItemReference ? !docItemReference.hasValue() : false;
      }
    );

    return billingRefPopulated && docItemRefsPopulated;
  }

  depositAmountLargerThanOutstandingBalance(): boolean {
    const depositPayment: DepositPayment = this.calculationResult.payments
      .deposit;

    return (
      !!this.deposit &&
      depositPayment &&
      depositPayment.isUnpaid() &&
      depositPayment.amountRemaining > this.calculationResult.outstandingBalance
    );
  }
}
